import axios from "axios";
import { inboxClient } from "./client";
import { instance as boompayClient } from "api/apiBase";
import type {
  ConversationsResponse,
  ConversationResponse,
  ConversationListParams,
  MessagesResponse,
  MessageResponse,
  SendEmailRequest,
  SendSmsRequest,
  SendEmailBatchRequest,
  SendSmsBatchRequest,
  BatchSendResponse,
  SendChatRequest,
  SendInAppMessageRequest,
  CreateConversationRequest,
  CreateManualNoteRequest,
  SearchBlockName,
  SearchConversationsResponse,
  GlobalSearchConversationsResponse,
  CallTranscriptData,
  SendOptionsResponse,
  SmsOptOutsResponse,
  InboxBadgeResponse,
  UndeliverableStatusResponse,
  InboxPreferences,
  MarkReadMode,
  SearchFilterParams,
  InboxFilterCountsResponse,
  TranslateRequest,
  TranslateResponse,
  AiReplyModeSetting,
  ConversationFlagClear,
  ConversationFlagKind,
  NoReplyNeededSelection,
  NoReplyNeededBatchResponse
} from "../types";

const BASE_PATH = "/api/portal/inbox";

export const inboxApi = {
  listConversations: async (params?: ConversationListParams) => {
    const { data } = await inboxClient.get<ConversationsResponse>(
      `${BASE_PATH}/conversations`,
      { params }
    );
    return data;
  },

  getInboxBadge: async () => {
    const { data } = await inboxClient.get<InboxBadgeResponse>(
      `${BASE_PATH}/conversations/inbox-badge`
    );
    return data;
  },

  getConversation: async (id: string) => {
    const { data } = await inboxClient.get<ConversationResponse>(
      `${BASE_PATH}/conversations/${id}`
    );
    return data;
  },

  getSendOptions: async (id: string) => {
    const { data } = await inboxClient.get<SendOptionsResponse>(
      `${BASE_PATH}/conversations/${id}/send-options`
    );
    return data;
  },

  // Lookup-only — never creates. Returns null when the backend 404s so
  // the caller can branch to the draft flow without try/catch noise.
  getConversationByContact: async (
    contactId: number
  ): Promise<ConversationResponse | null> => {
    try {
      const { data } = await inboxClient.get<ConversationResponse>(
        `${BASE_PATH}/conversations/by-contact/${contactId}`
      );
      return data;
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      throw e;
    }
  },

  createConversation: async (payload: CreateConversationRequest) => {
    const { data } = await inboxClient.post<ConversationResponse>(
      `${BASE_PATH}/conversations`,
      payload
    );
    return data;
  },

  getMessages: async (
    conversationId: string,
    params?: {
      limit?: number;
      cursor?: string;
      direction?: "before" | "after";
      channel?: "email" | "sms" | "note" | "chat" | "voice";
      anchor?: string;
      from?: string;
      to?: string;
      q?: string;
    }
  ) => {
    const { data } = await inboxClient.get<MessagesResponse>(
      `${BASE_PATH}/conversations/${conversationId}/messages`,
      { params }
    );
    return data;
  },

  translate: async (payload: TranslateRequest): Promise<TranslateResponse> => {
    const { data } = await inboxClient.post<TranslateResponse>(
      `${BASE_PATH}/translate`,
      payload
    );
    return data;
  },

  getSmsOptOuts: async (conversationId: string) => {
    const { data } = await inboxClient.get<SmsOptOutsResponse>(
      `${BASE_PATH}/conversations/${conversationId}/sms-opt-outs`
    );
    return data;
  },

  getUndeliverableStatus: async (phoneNumbers: string[]) => {
    const { data } = await inboxClient.post<UndeliverableStatusResponse>(
      `${BASE_PATH}/sms/undeliverable-status`,
      { phone_numbers: phoneNumbers }
    );
    return data;
  },

  getMessage: async (conversationId: string, messageId: string) => {
    const { data } = await inboxClient.get<MessageResponse>(
      `${BASE_PATH}/conversations/${conversationId}/messages/${messageId}`
    );
    return data;
  },

  markAsRead: async (conversationId: string) => {
    await inboxClient.patch(
      `${BASE_PATH}/conversations/${conversationId}/read`
    );
  },

  markAsUnread: async (conversationId: string) => {
    await inboxClient.patch(
      `${BASE_PATH}/conversations/${conversationId}/unread`
    );
  },

  getPreferences: async (): Promise<InboxPreferences> => {
    const { data } = await inboxClient.get<InboxPreferences>(
      `${BASE_PATH}/preferences`
    );
    return data;
  },

  updatePreferences: async (
    markReadMode: MarkReadMode
  ): Promise<InboxPreferences> => {
    const { data } = await inboxClient.put<InboxPreferences>(
      `${BASE_PATH}/preferences`,
      { mark_read_mode: markReadMode }
    );
    return data;
  },

  archiveConversation: async (conversationId: string) => {
    await inboxClient.patch(
      `${BASE_PATH}/conversations/${conversationId}/archive`
    );
  },

  reopenConversation: async (conversationId: string) => {
    await inboxClient.patch(
      `${BASE_PATH}/conversations/${conversationId}/reopen`
    );
  },

  retryEmail: async (messageId: string) => {
    const { data } = await boompayClient.post(
      `/portal/v1/communication_messages/${messageId}/retry`
    );
    return data;
  },

  deleteMessage: async (messageId: string) => {
    await boompayClient.delete(
      `/portal/v1/communication_messages/${messageId}`
    );
  },

  sendEmail: async (payload: SendEmailRequest) => {
    const { data } = await boompayClient.post<MessageResponse>(
      `/portal/v1/communication_messages/email`,
      payload
    );
    return data;
  },

  sendSms: async (payload: SendSmsRequest) => {
    const { data } = await inboxClient.post<MessageResponse>(
      `${BASE_PATH}/messages/sms`,
      payload
    );
    return data;
  },

  sendEmailBatch: async (payload: SendEmailBatchRequest) => {
    const { data } = await boompayClient.post<BatchSendResponse>(
      `/portal/v1/communication_messages/email/batch`,
      payload
    );
    return data;
  },

  sendSmsBatch: async (payload: SendSmsBatchRequest) => {
    const { data } = await inboxClient.post<BatchSendResponse>(
      `${BASE_PATH}/messages/sms/batch`,
      payload
    );
    return data;
  },

  // Native chat via communication-service; mirrored back into the timeline.
  sendChat: async (payload: SendChatRequest) => {
    const { data } = await inboxClient.post<MessageResponse>(
      `${BASE_PATH}/messages/chat`,
      payload
    );
    return data;
  },

  requestChatUpload: async (params: {
    filename: string;
    content_type: string;
    size: number;
  }): Promise<{ id: string; upload_url: string }> => {
    const { data } = await inboxClient.post<{ id: string; upload_url: string }>(
      `${BASE_PATH}/messages/attachments/request-upload`,
      params
    );
    return data;
  },

  // PUT straight to S3 (presigned URL — no auth header, so a bare axios call).
  uploadToPresignedUrl: async (
    uploadUrl: string,
    file: File
  ): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type }
    });
  },

  confirmChatUpload: async (id: string): Promise<void> => {
    await inboxClient.post(
      `${BASE_PATH}/messages/attachments/${id}/confirm-upload`
    );
  },

  setAiReplyMode: async (conversationId: string, mode: AiReplyModeSetting) => {
    const { data } = await inboxClient.patch<{
      ai_suggestions_enabled: boolean;
      ai_reply_mode_override: "suggest" | "auto_response" | null;
    }>(`${BASE_PATH}/conversations/${conversationId}/ai-reply-mode`, {
      mode
    });
    return data;
  },

  clearConversationFlag: async (
    conversationId: string,
    kind: ConversationFlagKind
  ) => {
    const { data } = await inboxClient.post<{ clear: ConversationFlagClear }>(
      `${BASE_PATH}/conversations/${conversationId}/flag-clears`,
      { kind }
    );
    return data;
  },

  undoConversationFlagClear: async (
    conversationId: string,
    clearId: string
  ) => {
    const { data } = await inboxClient.post<{ restored: boolean }>(
      `${BASE_PATH}/conversations/${conversationId}/flag-clears/${clearId}/undo`
    );
    return data;
  },

  // CRM-1931. Undo goes through undoConversationFlagClear with the returned id.
  markNoReplyNeeded: async (conversationId: string) => {
    const { data } = await inboxClient.post<{ clear: ConversationFlagClear }>(
      `${BASE_PATH}/conversations/${conversationId}/no-reply-needed`
    );
    return data;
  },

  // The query string carries the list's filters, so `{ all: true }` marks what
  // the list shows.
  markNoReplyNeededBulk: async (
    selection: NoReplyNeededSelection,
    params: SearchFilterParams
  ) => {
    const { data } = await inboxClient.post<NoReplyNeededBatchResponse>(
      `${BASE_PATH}/conversations/no-reply-needed`,
      selection,
      { params }
    );
    return data;
  },

  undoNoReplyNeededBatch: async (batchId: string) => {
    const { data } = await inboxClient.post<{ restored: number }>(
      `${BASE_PATH}/conversations/no-reply-needed/${batchId}/undo`
    );
    return data;
  },

  resolveSuggestion: async (payload: {
    anchor_message_id: string;
    final_sent_text: string;
    outbound_message_id?: string;
  }) => {
    const { data } = await inboxClient.post<{ ok: boolean; outcome?: string }>(
      `${BASE_PATH}/suggestions/resolve`,
      payload
    );
    return data;
  },

  dismissSuggestion: async (anchorMessageId: string) => {
    const { data } = await inboxClient.post<{ ok: boolean }>(
      `${BASE_PATH}/suggestions/dismiss`,
      { anchor_message_id: anchorMessageId }
    );
    return data;
  },

  retrySuggestion: async (anchorMessageId: string) => {
    const { data } = await inboxClient.post<{ ok: boolean }>(
      `${BASE_PATH}/suggestions/retry`,
      { anchor_message_id: anchorMessageId }
    );
    return data;
  },

  sendInAppMessage: async (payload: SendInAppMessageRequest) => {
    const { data } = await inboxClient.post<MessageResponse>(
      `${BASE_PATH}/messages/inapp`,
      payload
    );
    return data;
  },

  createManualNote: async (payload: CreateManualNoteRequest) => {
    const { data } = await boompayClient.post<MessageResponse>(
      `/portal/v1/communication_messages/note`,
      payload
    );
    return data;
  },

  getLeadDetails: async (leadId: string) => {
    const { data } = await inboxClient.get(
      `${BASE_PATH}/leads/${leadId}/details`
    );
    return data;
  },

  getApplicationDetails: async (applicationId: string) => {
    const { data } = await inboxClient.get(
      `${BASE_PATH}/applications/${applicationId}/details`
    );
    return data;
  },

  filterCounts: async (params?: SearchFilterParams) => {
    const { data } = await inboxClient.get<InboxFilterCountsResponse>(
      `${BASE_PATH}/conversations/filter-counts`,
      { params }
    );
    return data;
  },

  search: async (
    q: string,
    params?: {
      limit?: number;
      offset?: number;
      block?: SearchBlockName;
    } & SearchFilterParams
  ) => {
    const { data } = await inboxClient.get<SearchConversationsResponse>(
      `${BASE_PATH}/conversations/search`,
      {
        params: { q, ...params }
      }
    );
    return data;
  },

  globalSearch: async (q: string, limit: number, signal?: AbortSignal) => {
    const { data } = await inboxClient.get<GlobalSearchConversationsResponse>(
      `${BASE_PATH}/conversations/global-search`,
      { params: { q, limit }, signal }
    );
    return data;
  },

  getCallTranscript: async (
    callTranscriptId: number
  ): Promise<CallTranscriptData> => {
    const { data } = await boompayClient.get<CallTranscriptData>(
      `/portal/v1/call_transcripts/${callTranscriptId}`
    );
    return data;
  },

  // Outbound softphone calls — same response contract as getCallTranscript,
  // so both feed the shared CallTranscriptContent card.
  getOutboundCallTranscript: async (
    callId: string
  ): Promise<CallTranscriptData> => {
    const { data } = await boompayClient.get<CallTranscriptData>(
      `/portal/v1/boom_crm/calls/${callId}/transcript`
    );
    return data;
  }
};
